import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, ArrowUpRight } from 'lucide-react';

const Navbar = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [activeSection, setActiveSection] = useState('');

  const links = [
    { label: 'About Us', href: '#about' },
    { label: 'Services', href: '#services' },
    { label: 'How It Works', href: '#process' },
    { label: 'Case Studies', href: '#cases' },
    { label: 'Reviews', href: '#reviews' },
    { label: 'Tech Stack', href: '#tech' },
  ];

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = mobileOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [mobileOpen]);

  // Scroll-spy: detect which section is in view
  useEffect(() => {
    const sectionIds = links.map(l => l.href.replace('#', ''));
    const observers = [];

    sectionIds.forEach(id => {
      const el = document.getElementById(id);
      if (!el) return;
      const observer = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) setActiveSection(id);
        },
        { rootMargin: '-40% 0px -55% 0px', threshold: 0 }
      );
      observer.observe(el);
      observers.push(observer);
    });

    return () => observers.forEach(o => o.disconnect());
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleNavClick = (href) => {
    setMobileOpen(false);
    setTimeout(() => {
      const el = document.querySelector(href === '#' ? 'body' : href);
      if (el) el.scrollIntoView({ behavior: 'smooth' });
    }, 100);
  };

  return (
    <>
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled
            ? 'bg-[#060C1B]/95 backdrop-blur-xl border-b border-white/10 shadow-2xl py-3'
            : 'bg-[#060C1B]/60 backdrop-blur-md border-b border-white/5 py-4'
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between">

            {/* Brand Logo & Image */}
            <button
              onClick={() => handleNavClick('#')}
              className="group flex items-center gap-3 text-left bg-transparent border-0 cursor-pointer p-0"
            >
              <div className="relative w-10 h-10 rounded-xl overflow-hidden border border-white/15 shadow-md flex-shrink-0 group-hover:border-fuchsia-500/50 transition-colors">
                <img
                  src="/logo.png"
                  alt="Zynqora Edge Logo"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="flex flex-col">
                <span className="font-heading font-extrabold text-lg sm:text-xl tracking-tight text-white group-hover:text-fuchsia-400 transition-colors">
                  ZYNQORA EDGE
                </span>
                <span className="text-[9px] font-mono tracking-[0.2em] font-bold text-transparent bg-clip-text bg-gradient-to-r from-fuchsia-400 via-purple-400 to-cyan-400 uppercase">
                  INTELLIGENCE THAT SCALES
                </span>
              </div>
            </button>

            {/* Desktop Navigation Links */}
            <nav className="hidden lg:flex items-center gap-7">
              {links.map((link) => {
                const sectionId = link.href.replace('#', '');
                const isActive = activeSection === sectionId;
                return (
                  <a
                    key={link.label}
                    href={link.href}
                    onClick={(e) => {
                      e.preventDefault();
                      handleNavClick(link.href);
                    }}
                    className={`font-sans text-xs font-semibold uppercase tracking-wider transition-colors py-1 relative group ${
                      isActive ? 'text-white' : 'text-slate-400 hover:text-white'
                    }`}
                  >
                    {link.label}
                    <span
                      className={`absolute bottom-0 left-0 h-0.5 bg-gradient-to-r from-fuchsia-500 to-cyan-400 transition-all duration-200 ${
                        isActive ? 'w-full' : 'w-0 group-hover:w-full'
                      }`}
                    />
                  </a>
                );
              })}
            </nav>

            {/* Right Action CTA */}
            <div className="hidden sm:flex items-center gap-4">
              <button
                onClick={() => handleNavClick('#contact')}
                className="btn-primary text-xs uppercase tracking-wider font-semibold py-3 px-6 rounded-xl"
              >
                Book Strategy Call
                <ArrowUpRight size={15} strokeWidth={2.5} />
              </button>
            </div>

            {/* Mobile Hamburger Button */}
            <button
              onClick={() => setMobileOpen(!mobileOpen)}
              className="lg:hidden p-2 rounded-xl bg-slate-900 border border-white/10 text-slate-300 hover:text-white"
              aria-label="Toggle Navigation Menu"
            >
              {mobileOpen ? <X size={22} /> : <Menu size={22} />}
            </button>

          </div>
        </div>
      </header>

      {/* Mobile Menu Drawer */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 z-40 bg-[#060C1B]/98 backdrop-blur-2xl flex flex-col justify-center px-6 pt-24 pb-10 lg:hidden"
          >
            <div className="flex flex-col gap-5 text-center">
              {links.map((link) => {
                const sectionId = link.href.replace('#', '');
                const isActive = activeSection === sectionId;
                return (
                  <a
                    key={link.label}
                    href={link.href}
                    onClick={(e) => {
                      e.preventDefault();
                      handleNavClick(link.href);
                    }}
                    className={`font-heading text-xl font-bold uppercase tracking-wider transition-colors ${
                      isActive ? 'text-fuchsia-400' : 'text-slate-200 hover:text-fuchsia-400'
                    }`}
                  >
                    {link.label}
                  </a>
                );
              })}
              <div className="pt-6 border-t border-white/10 flex flex-col gap-4">
                <button
                  onClick={() => handleNavClick('#contact')}
                  className="btn-primary w-full justify-center text-sm py-4"
                >
                  Book Strategy Call
                  <ArrowUpRight size={16} />
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Navbar;
